import React, { useEffect, useState } from "react";
import { RotateCcw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const DEFAULTS = { avery_name_font_pt: 11, avery_meta_font_pt: 9, avery_line_gap_pt: 3 };
const STICKER_W_IN = 2.625;
const STICKER_H_IN = 1;

function pxPerInFor(width) {
  return width < 640 ? 70 : 150; // bigger, roomier view on desktop; shrunk to fit on mobile
}

function useResponsivePxPerIn() {
  const [px, setPx] = useState(() => pxPerInFor(typeof window !== "undefined" ? window.innerWidth : 1024));
  useEffect(() => {
    const onResize = () => setPx(pxPerInFor(window.innerWidth));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return px;
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

export function AverySettingsDialog({ open, onOpenChange, layout, onSave }) {
  const [draft, setDraft] = useState({ ...DEFAULTS, ...layout });
  const [saving, setSaving] = useState(false);
  const PX_PER_IN = useResponsivePxPerIn();
  const zoom = PX_PER_IN / 96; // preview is magnified beyond true CSS-inch scale on desktop
  const boxW = STICKER_W_IN * PX_PER_IN;
  const boxH = STICKER_H_IN * PX_PER_IN;

  useEffect(() => {
    if (open) setDraft({ ...DEFAULTS, ...layout });
  }, [open, layout]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({ ...layout, ...draft });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="avery-settings-dialog">
        <DialogHeader>
          <DialogTitle>Avery&reg; 5160 font size</DialogTitle>
          <DialogDescription>
            Adjust the Name line and Route/Tiffin line font sizes. Preview is proportionally accurate (2.625&times;1in) with a 1in grid for reference. Saved once — applies to every route printed with this style.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-2">
          <div
            className="flex flex-col items-start justify-start overflow-hidden rounded-sm border-2 border-dashed border-border bg-surface px-5 py-3"
            style={{
              width: boxW,
              height: boxH,
              backgroundImage:
                `repeating-linear-gradient(to right, transparent, transparent ${PX_PER_IN - 1}px, #E4E7DE ${PX_PER_IN - 1}px, #E4E7DE ${PX_PER_IN}px),` +
                `repeating-linear-gradient(to bottom, transparent, transparent ${PX_PER_IN - 1}px, #E4E7DE ${PX_PER_IN - 1}px, #E4E7DE ${PX_PER_IN}px)`,
            }}
            data-testid="avery-settings-preview"
          >
            <p className="font-bold leading-tight text-text-main" style={{ fontSize: `${draft.avery_name_font_pt * zoom}pt` }}>
              Customer Name
            </p>
            <p className="leading-tight text-text-muted" style={{ fontSize: `${draft.avery_meta_font_pt * zoom}pt`, marginTop: `${draft.avery_line_gap_pt * zoom}pt` }}>
              Route- 0000, Tiffin- 00
            </p>
          </div>
          <p className="mb-2 text-xs text-text-muted">2.625&times;1in sticker (each Avery 5160 label)</p>

          <div className="grid w-full grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Name line (pt)</Label>
              <Input
                type="number" step="0.5" min="4" max="40" value={draft.avery_name_font_pt}
                onChange={(e) => setDraft((d) => ({ ...d, avery_name_font_pt: clamp(parseFloat(e.target.value) || 4, 4, 40) }))}
                data-testid="avery-settings-name-font-input"
              />
            </div>
            <div>
              <Label className="text-xs">Route/Tiffin line (pt)</Label>
              <Input
                type="number" step="0.5" min="4" max="40" value={draft.avery_meta_font_pt}
                onChange={(e) => setDraft((d) => ({ ...d, avery_meta_font_pt: clamp(parseFloat(e.target.value) || 4, 4, 40) }))}
                data-testid="avery-settings-meta-font-input"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Line spacing (pt)</Label>
              <Input
                type="number" step="0.5" min="0" max="40" value={draft.avery_line_gap_pt}
                onChange={(e) => setDraft((d) => ({ ...d, avery_line_gap_pt: clamp(parseFloat(e.target.value) || 0, 0, 40) }))}
                data-testid="avery-settings-line-gap-input"
              />
            </div>
          </div>
          <Button
            variant="ghost" size="sm" className="w-fit self-start"
            onClick={() => setDraft((d) => ({ ...d, ...DEFAULTS }))}
            data-testid="avery-settings-reset-button"
          >
            <RotateCcw size={14} strokeWidth={1.5} /> Reset to default
          </Button>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)} data-testid="avery-settings-cancel-button">Cancel</Button>
          <Button variant="accent" onClick={handleSave} disabled={saving} data-testid="avery-settings-save-button">
            {saving ? "Saving..." : "Save settings"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
